import { useState, useEffect, useCallback, useMemo, useContext } from 'react'
import type { TableColumn } from 'react-data-table-component'

import { Button, MyTable, TableHeader } from '../../components'
import ContainerModal from '../../components/modals/ContainerModal'
import { AddEditUser, DeleteUser } from '../../components/modals/continental/admin/users'
import { useAppSelector } from '../../hooks/useReduxHooks'
import type { CustomUser, ModalOptions, Pagination } from '../../interfaces'
import { useGetEntityUsersQuery } from '../../store/api/identia'
import { skipToken } from '@reduxjs/toolkit/query'
import { selectAuth } from '../../store/slices'
import { AuthContext } from '../../context/auth/AuthProvider'

const DEFAULT_USER_PHOTO = 'https://www.fisheries.noaa.gov/themes/custom/noaa_components/images/ProfilePic_Placeholder.jpg'


const UsuariosB2BPage2 = () => {
    const [showAddEditUser, setShowAddEditUser] = useState<ModalOptions>('hide')
    const [showDeleteUser, setShowDeleteUser] = useState<boolean>(false)
    const { entity } = useAppSelector(selectAuth)
    const { user: authUser } = useContext(AuthContext)
    const [textQuery, setTextQuery] = useState<string>('')
    const [pagination, setPagination] = useState<Pagination>({
        actualPage: 1,
        itemCount: 25,
        total: 0,
    })

    const [currentUser, setCurrentUser] = useState<CustomUser>()

    const identiaQueryArg = entity
        ? { entityId: entity! || authUser.dataEntity._id, page: pagination.actualPage!, limit: pagination.itemCount!, search: textQuery, sortBy: 'name', order: 'asc' }
        : skipToken

    const { data: identiaData, isLoading, isFetching, refetch } = useGetEntityUsersQuery(identiaQueryArg as any)

    const columns: TableColumn<CustomUser>[] = useMemo(
        () => [
            {
                name: 'Foto',
                center: true,
                cell: (row) => (
                    <img
                        style={{
                            borderRadius: '50%',
                            objectFit: 'cover',
                        }}
                        src={row.photo}
                        onError={({ currentTarget }) => {
                            currentTarget.onerror = null
                            currentTarget.src = DEFAULT_USER_PHOTO
                        }}
                        alt={row.name}
                        height="30px"
                        width="30px"
                    />
                ),
            },
            {
                name: 'Nombre',
                selector: (row) => row.name,
            },
            {
                name: 'Apellido',
                selector: (row) => row.lastname,
            },
            {
                name: 'Email',
                selector: (row) => row.email,
            },
            {
                name: 'Teléfono',
                selector: (row) => row.phone,
            },
            {
                name: 'DNI',
                selector: (row) => row.dni,
            },
            {
                name: 'Dirección',
                selector: (row) => row.address,
            },
        ],
        [],
    )

    const openAddModal = useCallback(() => {
        setCurrentUser(undefined)
        setShowAddEditUser('add')
    }, [])

    const RowOptions = useCallback(
        () => (
            <div
                className="multiedu__table--row"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: '1rem',
                    padding: '1rem 1rem 1rem 6rem',
                }}
            >
                <Button
                    onClick={() => setShowAddEditUser('edit')}
                    text="Editar usuario"
                />
                <Button onClick={() => setShowDeleteUser(true)} text="Eliminar usuario" />
            </div>
        ),
        [],
    )

    useEffect(() => {
        if (identiaData) {
            setPagination((c) => ({
                ...c,
                actualPage: identiaData.page,
                itemCount: identiaData.limit,
                total: identiaData.total,
            }))
        }
    }, [identiaData])

    const users: CustomUser[] = (identiaData?.users || []).map((u: any) => ({
        _id: u.id || '',
        name: u.attributes?.name || '',
        lastname: u.attributes?.lastname || '',
        description: u.attributes?.description || '',
        dni: u.attributes?.dni || '',
        genre: u.attributes?.genre || '',
        place_of_birth: u.attributes?.place_of_birth || '',
        birthdate: null,
        phone: u.attributes?.phone || '',
        cv_url: u.attributes?.cv_url || '',
        address: u.attributes?.address || '',
        linkedin_url: u.attributes?.linkedin_url || '',
        photo: u.attributes?.photo || DEFAULT_USER_PHOTO,
        register: u.attributes?.register || '',
        rol: u.attributes?.rol || '',
        documentType: u.attributes?.documentType || '',
        email_verification: u.attributes?.email_verification || false,
        email: u.attributes?.email || '',
        code_verification: u.attributes?.code_verification || '',
        __v: 0,
        roles: (u.roles || []).map((r: any) => ({ _id: r.id || r._id || '', rol: r.name || r.rol || '' })),
    }))

    useEffect(() => {
        if (currentUser) {
            const updatedUser = users.find((u) => u._id === currentUser._id)
            if (updatedUser) {
                if (JSON.stringify(updatedUser) !== JSON.stringify(currentUser)) {
                    setCurrentUser(updatedUser)
                }
            } else {
                setCurrentUser(undefined)
            }
        }
    }, [users, currentUser])

    return (
        <>
            <div className="main_UsuariosB2bPage">
                <div className="headUsuariosB2b">
                    <h3>Usuarios</h3>
                </div>
                <div className="bodyUsuariosB2b scroll">
                    <div className="cardEntity_container">
                        <TableHeader
                            title="Listado de usuarios"
                            handleShowAddModal={openAddModal}
                            setTextQuery={setTextQuery}
                            refetch={refetch}
                        />
                        <MyTable
                            data={users}
                            columns={columns}
                            isLoading={isLoading || isFetching}
                            pagination={pagination}
                            setPagination={setPagination}
                            activeItem={currentUser}
                            setActiveItem={setCurrentUser}
                            refetch={refetch}
                            expandableRowsComponent={RowOptions}
                            showPagination
                        />
                    </div>
                </div>
            </div>
            <ContainerModal showModal={showAddEditUser !== 'hide'}>
                <AddEditUser
                    showModal={showAddEditUser}
                    setShowModal={setShowAddEditUser}
                    currentUser={currentUser}
                    setCurrentUser={setCurrentUser}
                />
            </ContainerModal>
            <ContainerModal showModal={showDeleteUser}>
                <DeleteUser setShowModal={setShowDeleteUser} userId={currentUser?._id!} />
            </ContainerModal>
        </>
    )
}

export default UsuariosB2BPage2
